/**
 * Centralized logging system for Peace EQ
 * Supports different log levels and buffered output for testing
 */

interface ComponentLogger {
    debug(message: string, ...args: any[]): void;
    info(message: string, ...args: any[]): void;
    warn(message: string, ...args: any[]): void;
    error(message: string, ...args: any[]): void;
    group(groupName: string, fn: () => any): void;
    groupAsync(groupName: string, fn: () => any): Promise<any>;
}

/**
 * Configurable logging system
 * Replaces console.log calls with structured, level-based logging
 */

// Log levels in order of severity
const LOG_LEVELS = {
    DEBUG: 0,
    INFO: 1, 
    WARN: 2,
    ERROR: 3,
    NONE: 4
};

const LOG_LEVEL_NAMES = {
    0: 'DEBUG',
    1: 'INFO',
    2: 'WARN', 
    3: 'ERROR',
    4: 'NONE'
};

const Logger = {
    // Current log level
    _currentLevel: LOG_LEVELS.WARN, // Default to WARN for production-like behavior
    
    // Enable/disable console output
    _consoleEnabled: true,
    
    // Log buffer for testing/debugging
    _logBuffer: [] as any[],
    _bufferEnabled: false,
    
    /**
     * Set the minimum log level
     * @param {string|number} level - Log level ('debug', 'info', 'warn', 'error', 'none') or number
     */
    setLevel(level: string | number): void {
        if (typeof level === 'string') {
            const upperLevel = level.toUpperCase();
            if ((LOG_LEVELS as any)[upperLevel] !== undefined) {
                this._currentLevel = (LOG_LEVELS as any)[upperLevel];
            } else {
                console.error('Invalid log level:', level);
            }
        } else if (typeof level === 'number') {
            if (level >= 0 && level <= 4) {
                this._currentLevel = level;
            } else {
                console.error('Invalid log level:', level);
            }
        }
    },
    
    /**
     * Get current log level
     * @returns {string} Current log level name
     */
    getLevel(): string {
        return (LOG_LEVEL_NAMES as any)[this._currentLevel];
    },
    
    /**
     * Enable or disable console output
     * @param {boolean} enabled - Whether console output is enabled
     */
    setConsoleEnabled(enabled: boolean): void {
        this._consoleEnabled = enabled;
    },
    
    /**
     * Enable or disable log buffering (useful for testing)
     * @param {boolean} enabled - Whether log buffering is enabled
     */
    setBufferEnabled(enabled: boolean): void {
        this._bufferEnabled = enabled;
        if (!enabled) {
            this._logBuffer = [];
        }
    },
    
    /**
     * Get logged messages from buffer
     * @returns {Array} Array of log entries
     */
    getBuffer() {
        return [...this._logBuffer];
    },
    
    /**
     * Clear the log buffer
     */
    clearBuffer() {
        this._logBuffer = [];
    },
    
    /**
     * Log a message at the specified level
     * @param {number} level - Log level
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    _log(level: number, message: string, ...args: any[]): void {
        // Check if this level should be logged
        if (level < this._currentLevel) return;
        
        const timestamp = new Date().toISOString();
        const levelName = (LOG_LEVEL_NAMES as any)[level];
        
        // Create log entry
        const logEntry = {
            timestamp,
            level: levelName,
            message,
            args: args.length > 0 ? args : undefined
        };
        
        // Add to buffer if enabled
        if (this._bufferEnabled) {
            this._logBuffer.push(logEntry);
            
            // Limit buffer size to prevent memory issues
            if (this._logBuffer.length > 1000) {
                this._logBuffer.shift();
            }
        }
        
        // Output to console if enabled
        if (this._consoleEnabled) {
            const formattedMessage = `[${timestamp}] ${levelName}: ${message}`;
            
            switch (level) {
                case LOG_LEVELS.DEBUG:
                    if (console.debug) {
                        console.debug(formattedMessage, ...args);
                    } else {
                        console.log(formattedMessage, ...args);
                    }
                    break;
                case LOG_LEVELS.INFO:
                    console.info(formattedMessage, ...args);
                    break;
                case LOG_LEVELS.WARN:
                    console.warn(formattedMessage, ...args);
                    break;
                case LOG_LEVELS.ERROR:
                    console.error(formattedMessage, ...args);
                    break;
                default:
                    console.log(formattedMessage, ...args);
            }
        }
    },
    
    /**
     * Log a debug message
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    debug(message: string, ...args: any[]): void {
        this._log(LOG_LEVELS.DEBUG, message, ...args);
    },
    
    /**
     * Log an info message
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    info(message: string, ...args: any[]): void {
        this._log(LOG_LEVELS.INFO, message, ...args);
    },
    
    /**
     * Log a warning message
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    warn(message: string, ...args: any[]): void {
        this._log(LOG_LEVELS.WARN, message, ...args);
    },
    
    /**
     * Log an error message
     * @param {string} message - Log message
     * @param {...any} args - Additional arguments
     */
    error(message: string, ...args: any[]): void {
        this._log(LOG_LEVELS.ERROR, message, ...args);
    },
    
    /**
     * Execute a function with grouped logging
     * @param {string} groupName - Group name
     * @param {Function} fn - Function to execute
     * @returns {any} Return value of the function
     */
    group(groupName: string, fn: () => any): any {
        if (this._consoleEnabled && console.group) {
            console.group(groupName);
        } else if (this._consoleEnabled) {
            console.log(`>>> ${groupName}`);
        }
        
        this.debug(`Starting group: ${groupName}`);
        
        try {
            const result = fn();
            this.debug(`Completed group: ${groupName}`);
            return result;
        } catch (error) {
            this.error(`Error in group ${groupName}:`, error);
            throw error;
        } finally {
            if (this._consoleEnabled && console.groupEnd) {
                console.groupEnd();
            } else if (this._consoleEnabled) {
                console.log(`<<< ${groupName}`);
            }
        }
    },
    
    /**
     * Execute an async function with grouped logging
     * @param {string} groupName - Group name
     * @param {Function} fn - Async function to execute
     * @returns {Promise} Promise that resolves to the function's return value
     */
    async groupAsync(groupName: string, fn: () => any): Promise<any> {
        if (this._consoleEnabled && console.group) {
            console.group(groupName);
        } else if (this._consoleEnabled) {
            console.log(`>>> ${groupName}`);
        }
        
        this.debug(`Starting async group: ${groupName}`);
        
        try {
            const result = await fn();
            this.debug(`Completed async group: ${groupName}`);
            return result;
        } catch (error) {
            this.error(`Error in async group ${groupName}:`, error);
            throw error;
        } finally {
            if (this._consoleEnabled && console.groupEnd) {
                console.groupEnd();
            } else if (this._consoleEnabled) {
                console.log(`<<< ${groupName}`);
            }
        }
    },
    
    /**
     * Create a logger instance for a specific component
     * @param {string} componentName - Name of the component
     * @returns {Object} Component-specific logger
     */
    createComponentLogger(componentName: string): ComponentLogger {
        return {
            debug: (message: string, ...args: any[]) => this.debug(`[${componentName}] ${message}`, ...args),
            info: (message: string, ...args: any[]) => this.info(`[${componentName}] ${message}`, ...args),
            warn: (message: string, ...args: any[]) => this.warn(`[${componentName}] ${message}`, ...args),
            error: (message: string, ...args: any[]) => this.error(`[${componentName}] ${message}`, ...args),
            group: (groupName: string, fn: () => any) => this.group(`${componentName}: ${groupName}`, fn),
            groupAsync: (groupName: string, fn: () => any) => this.groupAsync(`${componentName}: ${groupName}`, fn)
        };
    }
};

// Auto-configure based on environment
if (typeof window !== 'undefined') {
    // Browser environment
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.protocol === 'file:';
    
    Logger.setLevel(isDevelopment ? 'debug' : 'warn');
} else if ((() => {
    try {
        return typeof globalThis !== 'undefined' && (globalThis as any).process;
    } catch {
        return false;
    }
})()) {
    // Node.js environment 
    const nodeProcess = (globalThis as any).process;
    const isDevelopment = nodeProcess.env && nodeProcess.env.NODE_ENV === 'development';
    const isTest = nodeProcess.env && nodeProcess.env.NODE_ENV === 'test';
    
    if (isTest) {
        Logger.setLevel('error'); // Quiet during tests
        Logger.setBufferEnabled(true);
    } else {
        Logger.setLevel(isDevelopment ? 'debug' : 'warn');
    }
}

// ES module export
export { Logger, LOG_LEVELS };